import { FC, useEffect, useRef, useState } from "react";
import { useDataTableStore } from "../../stores/DataTableStore";
import useSWR from 'swr';
import axios from "axios";
import useAccessToken from "../../hooks/AccessToken";
import { Pipeline } from "../../interfaces/Pipeline";
import 'moment/locale/id';
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useIntl } from "react-intl";
import useUser from "../../hooks/User";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import 'primereact/resources/primereact.min.css';           // Core CSS
import 'primeicons/primeicons.css';                         // Icons
import { useSettingsStore } from "../../stores/SettingsStore";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useMerchantStore } from "../../stores/MerchantStore";
import { useUserStore } from "../../stores/UserStore";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDNDPipelineStore } from "../../stores/DNDPipelineStore";
import { LeadProgress, Progress } from "../../interfaces/Progress";
import { useProgressStore } from "../../stores/ProgressStore";
import { useVirtualizer } from "@tanstack/react-virtual";
import { MasterBoardPipelinesContent } from "../../app/pages/pipeline/MasterBoardPipelinesContent";
import { checkIsCompanyTanrise } from "../../functions/customCase";
import { useCustomeStore } from "../../stores/CustomeStore";


interface MasterDataTableProps {
    tableKey: string
    merchantId?: string
    apiURL: 'v2/leads'
    codeURL?: string
    dateStart?: string
    dateEnd?: string
    dateStartFilter?: string | null
    dateEndFilter?: string | null
    user_id?: string
    scope?: 'self' | 'team' | 'whole-team' | 'all'
    customParams?: any
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    autoReload?: boolean
    customState?: any
}

const MasterKanban: FC<MasterDataTableProps> = ({ tableKey, merchantId, apiURL, dateStart, dateEnd, user_id, scope, customParams, customState, setLoading, autoReload = true, dateStartFilter, dateEndFilter }) => {
    const API_URL = process.env.REACT_APP_API_URL
    const user = useUser()
    const parentRef = useRef<HTMLElement>(null);
    const { templatePipelineId, templateMeetingsId } = useParams()
    const { pipelines, selectMultiPipeline, pipelineLoading, preventPipelineFetch, setPreventPipelineFetch } = usePipelineStore()
    const { simpleMerchants, merchantLoading, selectMultiMerchant } = useMerchantStore()
    const { progress: progressDND, setProgress: setProgressDND } = useProgressStore()
    const { users, userLoading } = useUserStore()
    const [leadProgress, setLeadProgress] = useState<LeadProgress[]>([]);
    const { settings } = useSettingsStore()
    const { tablesData, setTablesData, filteredTablesData, setFilteredTablesData, setIsLoadingTable, setIsValidatingTable, isFullyLoaded, setIsFullyLoaded, allDataPipeline, setAllDataPipeline, setFetchCounter, fetchCounter } = useDataTableStore();
    const intl = useIntl()
    const BASE_URL = process.env.REACT_APP_API_URL
    const token = useAccessToken()
    const { companyId } = useCustomeStore()
    const { setCurrentDraggedPipelineCard, currentProgressOver, setCurrentProgressOver, setAllowedContainerToDrop, setIsCardDragging } = useDNDPipelineStore()
    const [isLoadingWorker, setIsLoadingWorker] = useState<boolean>(false)
    const defaultLeadLimit = 50
    let defaultLeadOffset = 0

    let params =
        apiURL === "v2/leads" && merchantId ?
            {
                leadTemplateId: templatePipelineId,
                merchant_id: merchantId,
                showAll: true
            }
            : apiURL === "v2/leads" && customParams && (customParams === "1" || customParams === 'CREATE' || customParams === 'NONE') ?
                {
                    leadTemplateId: templatePipelineId,
                    date_created_from: dateStartFilter ?? dateStart,
                    date_created_to: dateEndFilter ?? dateEnd,
                    user_id: user_id,
                    scope: scope,
                    leadOffset: defaultLeadOffset,
                    leadLimit: defaultLeadLimit
                }
                : apiURL === "v2/leads" && customParams && (customParams === "2" || customParams === "STARTED") ?
                    {
                        leadTemplateId: templatePipelineId,
                        date_start_from: dateStartFilter ?? dateStart,
                        date_start_to: dateEndFilter ?? dateEnd,
                        user_id: user_id,
                        scope: scope,
                        leadOffset: defaultLeadOffset,
                        leadLimit: defaultLeadLimit
                    }
                    : apiURL === "v2/leads" && customParams && (customParams === "3" || customParams === "ENDED") ?
                        {
                            leadTemplateId: templatePipelineId,
                            date_end_from: dateStartFilter ?? dateStart,
                            date_end_to: dateEndFilter ?? dateEnd,
                            user_id: user_id,
                            scope: scope,
                            leadOffset: defaultLeadOffset,
                            leadLimit: defaultLeadLimit
                        }
                        : apiURL === "v2/leads" && customParams && (customParams === "4") ?
                            {
                                leadTemplateId: templatePipelineId,
                                date_time_range_from: dateStartFilter ?? dateStart,
                                date_time_range_to: dateEndFilter ?? dateEnd,
                                user_id: user_id,
                                scope: scope,
                                leadOffset: defaultLeadOffset,
                                leadLimit: defaultLeadLimit
                            }
                            : {}

    const fetcher = async (url: string) => {
        const response = await axios.get(url, {
            headers: {
                'X-Auth-token': token,
            },
        });
        return response.data.data;
    };

    const { data: progresses = [], error: progressesError, isLoading: progressesLoading } = useSWR(`${API_URL}lead_progresses?onlyShowAllowedForCreateLeadLog=true&leadTemplateId=${templatePipelineId}`, fetcher);

    const columnVirtualizer = useVirtualizer({
        count: leadProgress.length,
        getScrollElement: () => parentRef.current ?? null,
        estimateSize: () => 350, // Sesuaikan dengan tinggi setiap item
        overscan: 1,
    });
    const items = columnVirtualizer.getVirtualItems()

    const fetchDataWithWorker = (url: string, params: any, token: string, selectMultiPipeline: any) => {
        const worker = new Worker(new URL('../DataTable/leadWorker.ts', import.meta.url));
        const newProgress = checkIsCompanyTanrise(companyId, user) ? intl.formatMessage({ id: "FORM.LABEL.CONTACTED" }) : intl.formatMessage({ id: "FORM.LABEL.NEW" })
        setLoading(true)
        setIsLoadingWorker(true)
        worker.postMessage({
            url,
            params,
            token,
            defaultLeadLimit: 100,
            selectMultiPipeline,
            settings,
            selectMultiMerchant,
            pipelines,
            users,
            simpleMerchants,
            newProgress,
            sortField: 'date_created',
            sortDirection: 'desc',
            user
        });

        worker.onmessage = (event) => {
            const { data, done, error, dataIsEmpty } = event.data;

            if (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error,
                })
            }

            if (data) {
                setLoading(false)
                setIsLoadingWorker(false)
                if (tableKey === "pipelines") {
                    data.sort((a: any, b: any) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime())
                }

                setTimeout(() => {
                    setTablesData(tableKey, data)
                    setFilteredTablesData(tableKey, data)
                }, 10)
            }

            if (done) {
                worker.terminate();
                setIsFullyLoaded(true);
                setPreventPipelineFetch(false)
                if (dataIsEmpty) {
                    setLoading(false)
                    setIsLoadingWorker(false)
                }
            }

        };
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.data.current.type === 'card') {
            if (over.id === "-1") {
                setCurrentProgressOver(undefined)
                setCurrentDraggedPipelineCard(undefined)
                setIsCardDragging(false)
                return;
            }

            const pipeline: Pipeline = filteredTablesData['pipelines'].filter((pipeline: Pipeline) => pipeline.id === active.id)[0]

            customState.setPipeline(pipeline)

            if (progresses.some((progres: any) => progres.id === currentProgressOver)) {
                customState.setShowAddLogModal(true);

            } else {
                setCurrentProgressOver(undefined)
                setCurrentDraggedPipelineCard(undefined)
                setIsCardDragging(false)
            }
        }

        if (active.id !== over.id && active.data.current.type !== 'card') {
            const updateProgress = (leadProgress: LeadProgress[]) => {
                const oldIndex = leadProgress.findIndex(item => item.id === active.id);
                const newIndex = leadProgress.findIndex(item => item.id === over.id);
                return arrayMove(leadProgress, oldIndex, newIndex);
            };

            const newLeadProgress = updateProgress([...leadProgress]);
            setLeadProgress(newLeadProgress);
            if (!templatePipelineId) return 0;
            const newLeadsProgressDND = { ...progressDND }; // copy object agar tetap immutable
            newLeadsProgressDND[templatePipelineId] = newLeadProgress;
            setProgressDND(newLeadsProgressDND);


        }

        setCurrentDraggedPipelineCard(undefined)
    };

    const handleDragOver = (event: any) => {
        if (event.active.data.current.type === 'card') {
            setCurrentProgressOver(event.over.id)
        }
        return;
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5, // untuk menghindari drag yang tidak disengaja
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );

    useEffect(() => {
        // Simpan nilai awal
        const originalOverflow = document.body.style.overflowY;

        // Nonaktifkan scroll vertikal
        document.body.style.overflowY = 'hidden';

        return () => {
            // Kembalikan scroll saat komponen unmount
            document.body.style.overflowY = originalOverflow;
        };
    }, []);

    useEffect(() => {
        const progressesID: string[] = progresses.map((progress: Progress) => progress.id)
        setAllowedContainerToDrop(progressesID)
    }, [progresses])

    useEffect(() => {
        if (filteredTablesData["pipelines"] && progressDND && progressDND[templatePipelineId!]) {
            if (progressDND[templatePipelineId!].length !== 0) {
                var leadProgressName = [...new Map(filteredTablesData["pipelines"].map((item) => [item.lead_progress_name, { id: item.lead_progress_id === null ? "-1" : item.lead_progress_id, name: item.lead_progress_name, color: item.lead_progress_color, sort: "asc" }])).values()];

                setLeadProgress([...progressDND[templatePipelineId!].filter(item => leadProgressName.some(progres => progres.name === item.name)), ...leadProgressName.filter(item => !progressDND[templatePipelineId!].some(progres => progres.name === item.name))]);
            }
        }
    }, [filteredTablesData["pipelines"], progressDND])

    useEffect(() => {
        if (filteredTablesData["pipelines"] && (!progressDND || !progressDND[templatePipelineId!])) {
            const orderType = ["ONGOING", "SUCCESS", "FAIL"]
            filteredTablesData["pipelines"].sort((a, b) => {
                if ((a.lead_progress_name).includes("NEW")) return -1;
                if ((b.lead_progress_name).includes("NEW")) return 1;

                const indexA = orderType.indexOf(a.lead_progress_progress_type);
                const indexB = orderType.indexOf(b.lead_progress_progress_type);
                return indexA - indexB;
            });

            var leadProgressName = [...new Map(filteredTablesData["pipelines"].map((item) => [item.lead_progress_name, { id: item.lead_progress_id === null ? "-1" : item.lead_progress_id, name: item.lead_progress_name, color: item.lead_progress_color, sort: "asc" }])).values()];
            let newProgressDNDprogressDND = progressDND ?? {}
            newProgressDNDprogressDND[templatePipelineId!] = leadProgressName
            setProgressDND(newProgressDNDprogressDND)
        }
    }, [filteredTablesData["pipelines"]])

    useEffect(() => {
        if (tableKey === 'pipelines' && fetchCounter > 0 && !preventPipelineFetch) { //counterLeadWorker === 1
            setIsFullyLoaded(false)
            fetchDataWithWorker(BASE_URL + apiURL, params, token, selectMultiPipeline);
        }
    }, [fetchCounter, user_id, scope]);
    return (
        <>
            {
                isLoadingWorker &&
                <div className="loading-overlay">
                    <div className="loader"></div>
                </div>
            }

            <DndContext onDragEnd={handleDragEnd} onDragOver={handleDragOver} sensors={sensors} collisionDetection={closestCenter}>
                <SortableContext items={leadProgress} strategy={horizontalListSortingStrategy}>
                    <section ref={parentRef}>
                        <div
                            style={{
                                height: columnVirtualizer.getTotalSize(),
                                width: '100%',
                                position: 'relative',
                            }}>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateX(${items[0]?.start ?? 0}px)`,
                                }}
                                className="lists-container position-relative">
                                {columnVirtualizer.getVirtualItems().map((virtualRow) => {
                                    const progress = leadProgress[virtualRow.index];
                                    return (
                                        <MasterBoardPipelinesContent
                                            key={progress.id}
                                            id={progress.id}
                                            dataProgress={progress}
                                            data={filteredTablesData["pipelines"].filter((pipeline) => pipeline.lead_progress_name === progress.name)}
                                            setShowUpdateModal={customState.setShowUpdateModal}
                                            setShowDeleteModal={customState.setShowDeleteModal}
                                            setShowAddLogModal={customState.setShowAddLogModal}
                                            setShowDetailModal={customState.setShowDetailModal}
                                            setShowDetailProductModal={customState.setShowDetailProductModal}
                                            setPipeline={customState.setPipeline}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                </SortableContext>
            </DndContext>
        </>
    )
}

export { MasterKanban }